/**
 * Visual indicator for the LineTool's adapter-level constraint snap.
 *
 * Renders a dashed line from the candidate point to the foot on the
 * reference segment plus a text label showing the snapped distance
 * (e.g. "5 cm" or "1.20 m"). Owned by the shared constraint-snap hook so
 * both `ManualLineMode` and `AutomaticLineMode` get the same indicator.
 *
 * The visual lives in the LineTool's plane-preview scene (same scene as
 * `LinePromptVisualizer`'s `linePreviewVisual`) so it inherits the
 * top-of-stack render order without fighting the prompt visuals.
 */

import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { TextSprite } from '../../../../TextSprite';
import { Utils } from '../../../../utils';
import type { Viewer } from '../../../../viewer/viewer';
import type { ConstraintSnapResult } from './LineConstraintSnap';

export class ConstraintSnapVisuals {
  private group: THREE.Group | null = null;
  private line: Line2 | null = null;
  private lineGeometry: LineGeometry | null = null;
  private lineMaterial: LineMaterial | null = null;
  private label: TextSprite | null = null;

  constructor(
    private readonly viewer: Viewer,
    private readonly scene: THREE.Scene,
  ) {}

  /**
   * Show the snap indicator: dashed line from the snap target (`adjusted`)
   * to its perpendicular foot on the reference line (`foot`), with a label
   * at the midpoint showing the perpendicular distance.
   */
  show(_candidateWorld: THREE.Vector3, result: ConstraintSnapResult, planeZ: number): void {
    this.clear();
    this.group = new THREE.Group();

    const adjustedWorld = new THREE.Vector3(result.adjusted.x, result.adjusted.y, planeZ);
    const footWorld = new THREE.Vector3(result.foot.x, result.foot.y, planeZ);

    // Anchor the group at the snap target so the local frame is small and
    // floating-point precision stays good even at large world coords.
    const origin = adjustedWorld.clone();
    this.group.position.copy(origin);

    const localAdjusted = new THREE.Vector3(0, 0, 0);
    const localFoot = footWorld.clone().sub(origin);

    this.lineGeometry = new LineGeometry();
    this.lineGeometry.setPositions([localAdjusted.x, localAdjusted.y, localAdjusted.z, localFoot.x, localFoot.y, localFoot.z]);
    this.lineMaterial = new LineMaterial({
      color: 0x88ddff,
      linewidth: 2,
      resolution: new THREE.Vector2(this.viewer.renderArea.clientWidth, this.viewer.renderArea.clientHeight),
      depthTest: false,
      transparent: true,
      opacity: 0.9,
      dashed: true,
      dashScale: 10,
      dashSize: 1,
      gapSize: 0.5,
    });
    this.line = new Line2(this.lineGeometry, this.lineMaterial);
    this.line.computeLineDistances();
    this.line.renderOrder = 1100;
    this.group.add(this.line);

    this.label = new TextSprite(result.label);
    this.label.setBackgroundColor({ r: 0, g: 0, b: 0, a: 0.7 });
    this.label.setBorderColor({ r: 0, g: 0, b: 0, a: 1 });
    this.label.setTextColor({ r: 255, g: 255, b: 255, a: 1 });
    this.label.material.depthTest = false;
    this.label.renderOrder = 1101;
    // Midpoint of the snap-target → foot segment so the distance reads
    // along the line being measured.
    const midLocal = localFoot.clone().multiplyScalar(0.5);
    this.label.position.copy(midLocal);
    this.scaleLabel(this.label, adjustedWorld);
    this.group.add(this.label);

    this.scene.add(this.group);
  }

  /** Hide and dispose the indicator. */
  clear(): void {
    if (this.group) {
      this.scene.remove(this.group);
      this.group = null;
    }
    this.lineGeometry?.dispose();
    this.lineGeometry = null;
    this.lineMaterial?.dispose();
    this.lineMaterial = null;
    this.line = null;
    this.label = null;
  }

  /** Keep the label legible regardless of camera distance/zoom. */
  private scaleLabel(label: TextSprite, anchorWorld: THREE.Vector3): void {
    const camera = this.viewer.scene.getActiveCamera();
    const size = this.viewer.renderer.getSize(new THREE.Vector2());
    const distance = camera.position.distanceTo(anchorWorld);
    const pr = Utils.projectedRadius(1, camera, distance, size.width, size.height);
    const scale = 70 / Math.max(pr, 0.0001);
    label.scale.set(scale, scale, scale);
  }
}
